import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AddressSchema, Entity } from '@debbie/common/dist/index.js';
import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { getContactPerson } from '@debbie/common/dist/customers/index.js';
import { InteractionType, Direction } from '@debbie/common/dist/interactions/index.js';
import { selectCustomerById } from '@debbie/cortex/dist/customers/selectors.js';
import { addressToKVHX } from '@debbie/common/dist/addresses/index.js';
import { patchUser } from '@debbie/cortex/dist/api/customers.js';
import { FileText } from '../Icons/index.js';
import FileIcon from '../Icons/File.js';
import Inbox from '../Icons/Inbox.js';
import Mail from '../Icons/Mail.js';
import MessageCircle from '../Icons/MessageCircle.js';
import Phone from '../Icons/Phone.js';
import SettingsIcon from '../Icons/Settings.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
import AutoCallInteraction from './AutoCallInteraction.js';
import CallInteraction from './CallInteraction.js';
import EBoksInteraction from './EBoksInteraction.js';
import EmailInteraction from './EmailInteraction.js';
import LetterInteraction from './LetterInteraction.js';
import NoteInteraction from './NoteInteraction.js';
import Procedures from './Procedures.js';
import RecipientContent from './RecipientContent.js';
import SMSInteraction from './SMSInteraction.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { useEmailServer } from '@debbie/cortex/dist/email-servers/index.js';
const CaseContactPerson = (props) => {
    const singleCase = props.case;
    const [KVHX, setKVHX] = useState(null);
    const creditorLoadable = useCreditor(singleCase?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const customer = useSelector((s) => singleCase && selectCustomerById(s, singleCase.customerId));
    const collectorLoadable = useCollector((singleCase &&
        creditor &&
        getCollectorOnCase(singleCase.type, singleCase.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId)) ||
        undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const emailServerLoadable = useEmailServer(collector?.emailServerId);
    const emailServer = h.hasData(emailServerLoadable)
        ? emailServerLoadable.data
        : null;
    const lang = useSelector((s) => s.lang);
    const contactPerson = customer && getContactPerson(customer);
    // Only in Denmark
    const loadKVHX = async () => {
        if (contactPerson?.details.address && collector?.eBoksSetup?.activated) {
            const address = contactPerson.details.address;
            const _kvhx = await addressToKVHX(address);
            const newAddress = {
                ...address,
                KVHX: _kvhx,
            };
            const validation = AddressSchema.safeParse(newAddress);
            if (_kvhx && validation.error != null && address.KVHX == null) {
                // Update kvhx for address
                patchUser(contactPerson.customerId, contactPerson.userId, {
                    details: {
                        address: newAddress,
                    },
                });
            }
            if (_kvhx) {
                setKVHX(_kvhx);
            }
        }
    };
    useEffect(() => {
        loadKVHX();
    }, [contactPerson, collector]);
    const emailTo = contactPerson && contactPerson.details.email;
    const letterTo = contactPerson &&
        contactPerson.details.address &&
        contactPerson.details.address.address &&
        contactPerson.details.name
        ? contactPerson.details.name
        : null;
    const centralRegisterId = customer && contactPerson
        ? customer.isCompany
            ? contactPerson.details.cvr
            : contactPerson.details.cpr
        : null;
    const eboksAvailable = collector?.eBoksSetup?.activated
        ? centralRegisterId
            ? centralRegisterId
            : KVHX
        : false;
    const hasPhone = !!(collector &&
        collector.name &&
        contactPerson &&
        contactPerson.details.phone);
    if (!(creditor && collector && singleCase && contactPerson))
        return (React.createElement("div", null,
            React.createElement("br", null),
            React.createElement("br", null),
            React.createElement("br", null),
            React.createElement(PlaceholderLoader, { width: "80%" }),
            React.createElement(PlaceholderLoader, { width: "35%" }),
            React.createElement(PlaceholderLoader, { width: "66%" })));
    const tabs = [
        {
            children: collector && creditor && (React.createElement(Procedures, { caseId: singleCase.caseId, caseCollectorId: collector.collectorId, tenantId: collector.tenantId, caseCreditorId: creditor.creditorId })),
            icon: SettingsIcon,
            label: lang.interactions.procedures,
        },
        {
            children: (React.createElement(NoteInteraction, { referenceId: singleCase.caseId, referenceType: Entity.Case, id: singleCase.id, options: {
                    direction: Direction.OUTGOING,
                    type: InteractionType.DebtorNote,
                }, sendButtonText: lang.interactions.sendNoteToDebtor })),
            icon: MessageCircle,
            label: lang.interactionTypes['DEBTOR NOTE'],
            shouldHide: !emailTo || !emailServer,
        },
        {
            children: (React.createElement(EmailInteraction, { referenceId: singleCase.caseId, referenceType: Entity.Case, isDebtor: true, emailTo: emailTo })),
            className: 'ui-test-d77b5bf4',
            icon: Inbox,
            label: lang.interactionTypes.EMAIL,
            shouldHide: !emailTo || !emailServer,
        },
        {
            children: (React.createElement(LetterInteraction, { case: singleCase, customer: customer, isDebtor: true })),
            icon: Mail,
            label: lang.interactionTypes.LETTER,
            shouldHide: !letterTo,
        },
        {
            children: (React.createElement(EBoksInteraction, { case: singleCase, customer: customer, isDebtor: true, KVHX: KVHX, referenceId: singleCase.caseId, referenceType: Entity.Case })),
            icon: FileText,
            label: lang.interactionTypes.EBOKS,
            shouldHide: !eboksAvailable,
        },
        {
            children: (React.createElement(CallInteraction, { referenceId: singleCase.caseId, referenceType: Entity.Case, outboundMode: props.outboundMode })),
            icon: Phone,
            label: lang.interactionTypes.PHONE,
            defaultIndex: props.outboundMode,
        },
        {
            children: (React.createElement(SMSInteraction, { case: singleCase, customer: customer, isDebtor: true })),
            icon: Phone,
            label: lang.interactionTypes.SMS,
            shouldHide: !hasPhone,
        },
        {
            children: React.createElement(AutoCallInteraction, { case: singleCase, customer: customer }),
            icon: Phone,
            label: lang.interactionTypes['AUTO CALL'],
            shouldHide: !hasPhone,
        },
        {
            children: (React.createElement(NoteInteraction, { referenceId: singleCase.caseId, referenceType: Entity.Case, id: singleCase.id, options: {
                    direction: Direction.OUTGOING,
                    type: InteractionType.Note,
                }, sendButtonText: lang.interactions.addNote })),
            icon: FileIcon,
            label: lang.interactionTypes.NOTE,
        },
    ];
    return React.createElement(RecipientContent, { tabs: tabs });
};
export default CaseContactPerson;
//# sourceMappingURL=CaseContactPerson.js.map